/**
 * Skip BuyOpen Confirmation Dialog Component
 *
 * Asks user to confirm before skipping the BuyOpen state.
 * Skip proceeds to the next state with whatever volume has been filled so far.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import FastForwardIcon from '@mui/icons-material/FastForward';
import type { StrategyListItemV2 } from '../../types/strategy';
import { traderTheme as theme } from './traderDashboardTheme';

export interface SkipConfirmDialogProps {
  open: boolean;
  strategy: StrategyListItemV2 | null;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}

export const SkipConfirmDialog: React.FC<SkipConfirmDialogProps> = ({
  open,
  strategy,
  onClose,
  onConfirm,
  loading,
}) => {
  const { t } = useTranslation();

  if (!strategy) return null;

  const symbol = strategy.symbol || '-';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: theme.bg.primary,
          borderBottom: `1px solid ${theme.border}`,
          color: theme.warning,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <FastForwardIcon />
        {t('traderDashboard.skipConfirm.title', { symbol })}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.bg.primary, pt: 3 }}>
        <Typography variant="body1" sx={{ color: theme.text.primary, mb: 2 }}>
          {t('traderDashboard.skipConfirm.confirmMessage')}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: theme.text.secondary, mb: 1 }}>
            {t('traderDashboard.skipConfirm.description')}
          </Typography>
          <Box component="ul" sx={{ color: theme.text.secondary, pl: 2, m: 0 }}>
            <li>
              <Typography variant="body2">
                {t('traderDashboard.skipConfirm.proceedWithFilled')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('traderDashboard.skipConfirm.ordersRemain')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('traderDashboard.skipConfirm.monitorInBackground')}
              </Typography>
            </li>
          </Box>
        </Box>

      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: theme.bg.primary,
          borderTop: `1px solid ${theme.border}`,
          p: 2,
        }}
      >
        <Button onClick={onClose} sx={{ color: theme.text.secondary }} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: theme.text.primary }} /> : <FastForwardIcon />}
          sx={{
            backgroundColor: theme.warning,
            color: theme.bg.primary,
            '&:hover': {
              backgroundColor: '#b8860b',
            },
            '&.Mui-disabled': {
              backgroundColor: theme.bg.secondary,
              color: theme.text.secondary,
            },
          }}
        >
          {loading ? t('traderDashboard.skipConfirm.skipping') : t('traderDashboard.skipConfirm.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkipConfirmDialog;
